import {GLOBAL} from "../../../common/globals";
import type {EopPopover} from "./popover";
import {Resolution} from "../../../common/resolution";
import {resolve} from "../../../container";
import {Page} from "../../../common/page";
import {eventOccurredOutside} from "../../../common/utils/events";
import {BOOTSTRAP} from "../../../common/resolutionConstants";

const ADDITIONAL_TOOLTIP_PADDING_MOBILE = 10;
const ADDITIONAL_TOOLTIP_PADDING_DESKTOP = 25;
const POPOVER_ABOVE_CLASS = "popover-above";
const POPOVER_BELOW_CLASS = "popover-below";
const POPOVER_ON_LEFT_EDGE_CLASS = "popover-on-left-edge";
const POPOVER_ON_RIGHT_EDGE_CLASS = "popover-on-right-edge";
const POPOVER_IN_CENTER = "popover-in-center";
//has to be in sync with the scss variable $popover-arrow-height
const POPOVER_ARROW_HEIGHT = 11;

export class TooltipDisplayMode {
    private outsideClickHandler: ((event: Event) => void) | null = null;
    private popoverHeight: number = 0;
    private popoverWidth: number = 0;

    public constructor(
        private popover: EopPopover,
        private anchor: HTMLElement,
        private popoverElement: HTMLElement,
        private resolution: Resolution = resolve(Resolution),
        private page: Page = resolve(Page)
    ) {
    }

    public open(): void {
        this.outsideClickHandler = event => {
            if (eventOccurredOutside(event, this.anchor) && eventOccurredOutside(event, this.popoverElement)) {
                this.close();
            }
            event.stopPropagation();
        };
        this.popover.silentlyShowAfter(() => this.adjustPopoverOffset());

        GLOBAL.bodyElement().addEventListener("click", this.outsideClickHandler);
        GLOBAL.bodyElement().addEventListener("touchstart", this.outsideClickHandler);
        this.popoverElement.addEventListener("click", this.outsideClickHandler);
        this.popoverElement.addEventListener("touchstart", this.outsideClickHandler);
    }

    public close(): void {
        if (this.outsideClickHandler) {
            GLOBAL.bodyElement().removeEventListener("click", this.outsideClickHandler);
            GLOBAL.bodyElement().removeEventListener("touchstart", this.outsideClickHandler);
            this.popoverElement.removeEventListener("click", this.outsideClickHandler);
            this.popoverElement.removeEventListener("touchstart", this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        this.popover.doHide();
    }

    public adjustPopoverOffset(): void {
        this.resetLayout();
        this.setAdditionalPadding();
        this.setPopoverHeight();
        this.setPopoverWidth();
        this.adjustHorizontalOffset();
        this.adjustVerticalOffset();
    }

    private resetLayout(): void {
        [
            POPOVER_ABOVE_CLASS,
            POPOVER_BELOW_CLASS,
            POPOVER_IN_CENTER,
            POPOVER_ON_LEFT_EDGE_CLASS,
            POPOVER_ON_RIGHT_EDGE_CLASS
        ].forEach(className => this.popover.popoverContainer.classList.remove(className));
        this.popover.popoverElement.style.left = "";
    }

    private adjustHorizontalOffset(): void {
        if (this.isOnLeftEdge()) {
            this.popover.popoverContainer.classList.add(POPOVER_ON_LEFT_EDGE_CLASS);
        } else if (this.isOnRightEdge()) {
            this.popover.popoverContainer.classList.add(POPOVER_ON_RIGHT_EDGE_CLASS);
        } else {
            this.popover.popoverContainer.classList.add(POPOVER_IN_CENTER);
        }
    }

    private adjustVerticalOffset(): void {
        if (this.isOnTopEdge()) {
            this.popover.popoverContainer.classList.add(POPOVER_BELOW_CLASS);
        } else {
            this.popover.popoverContainer.classList.add(POPOVER_ABOVE_CLASS);
        }
    }

    private isOnLeftEdge(): boolean {
        return this.distanceToLeftEdge() < this.minimumDistanceToEdge();
    }

    private isOnRightEdge(): boolean {
        return this.distanceToRightEdge() < this.minimumDistanceToEdge();
    }

    private minimumDistanceToEdge(): number {
        return this.getAdditionalPadding() + this.getPopoverWidth() * 0.5;
    }

    private isOnTopEdge(): boolean {
        return this.distanceToTopEdge() - this.getPopoverHeight() - POPOVER_ARROW_HEIGHT <= 0;
    }

    private distanceToLeftEdge(): number {
        return this.leftOffset();
    }

    private distanceToRightEdge(): number {
        return this.page.viewportWidth() - this.leftOffset();
    }

    private leftOffset(): number {
        return this.anchor.leftOffset();
    }

    private distanceToTopEdge(): number {
        return this.anchor.topOffset() - this.page.getEffectiveYScrollPosition();
    }

    private getPopoverWidth(): number {
        return this.popoverWidth;
    }

    private setPopoverWidth(): void {
        this.popoverWidth = this.popover.popoverElement.clientWidth;
    }

    private getPopoverHeight(): number {
        return this.popoverHeight;
    }

    private setPopoverHeight(): void {
        this.popoverHeight = this.popover.popoverElement.clientHeight;
        this.popover.popoverContainer.style.setProperty("--popover-height", `${this.getPopoverHeight()}px`);
    }

    private getAdditionalPadding(): number {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            return ADDITIONAL_TOOLTIP_PADDING_MOBILE;
        }
        return ADDITIONAL_TOOLTIP_PADDING_DESKTOP;
    }

    private setAdditionalPadding(): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            this.popover.popoverContainer.style.setProperty("--additional-padding", `${ADDITIONAL_TOOLTIP_PADDING_MOBILE}px`);
        } else {
            this.popover.popoverContainer.style.setProperty("--additional-padding", `${ADDITIONAL_TOOLTIP_PADDING_DESKTOP}px`);
        }
    }
}
